<template>
  <section class="emoji-picker" role="dialog" aria-label="Emoji-Auswahl" @keydown.esc.stop.prevent="$emit('close')">
    <div class="emoji-search">
      <MagnifyingGlassIcon aria-hidden="true" />
      <input ref="searchInput" v-model="query" type="search" aria-label="Emoji suchen" placeholder="Emoji suchen …" />
    </div>
    <div class="emoji-results">
      <section v-for="group in filteredGroups" :key="group.title" class="emoji-group" :aria-label="group.title">
        <h3>{{ group.title }}</h3>
        <div class="emoji-grid">
          <button
            v-for="[emoji, label] in group.emojis"
            :key="emoji"
            type="button"
            :aria-label="label"
            :title="label"
            @click="$emit('select', emoji)"
          >{{ emoji }}</button>
        </div>
      </section>
      <p v-if="!filteredGroups.length" class="emoji-empty" role="status">Keine Emojis gefunden.</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';

defineEmits<{
  (event: 'select', emoji: string): void;
  (event: 'close'): void;
}>();

const searchInput = ref<HTMLInputElement | null>(null);
const query = ref('');
const groups = [
  {
    title: 'Smileys',
    emojis: [
      ['😀', 'Grinsen'], ['😃', 'Fröhlich'], ['😄', 'Lachen'], ['😁', 'Strahlen'],
      ['😊', 'Lächeln'], ['🙂', 'Leichtes Lächeln'], ['😉', 'Zwinkern'], ['😂', 'Freudentränen'],
      ['🤣', 'Lachend am Boden'], ['😍', 'Verliebt'], ['🥰', 'Glücklich mit Herzen'], ['😎', 'Cool'],
      ['🤔', 'Nachdenken'], ['😅', 'Erleichtert lachen'], ['😐', 'Neutral'], ['🙃', 'Verkehrt herum'],
      ['😕', 'Verwirrt'], ['😟', 'Besorgt'], ['😢', 'Traurig'], ['😭', 'Weinen'],
      ['😮', 'Überrascht'], ['😴', 'Schlafen'], ['🥳', 'Feiern'], ['🤗', 'Umarmung'],
    ],
  },
  {
    title: 'Gesten',
    emojis: [
      ['👋', 'Winken'], ['👍', 'Daumen hoch'], ['👎', 'Daumen runter'], ['👌', 'Okay'],
      ['✌️', 'Frieden'], ['🤞', 'Daumen drücken'], ['👏', 'Applaus'], ['🙌', 'Jubel'],
      ['🙏', 'Danke'], ['🤝', 'Händedruck'], ['💪', 'Stärke'], ['🫶', 'Herzhände'],
      ['👆', 'Nach oben zeigen'], ['👇', 'Nach unten zeigen'], ['👉', 'Nach rechts zeigen'], ['👈', 'Nach links zeigen'],
    ],
  },
  {
    title: 'Herzen',
    emojis: [
      ['❤️', 'Rotes Herz'], ['🧡', 'Oranges Herz'], ['💛', 'Gelbes Herz'], ['💚', 'Grünes Herz'],
      ['💙', 'Blaues Herz'], ['💜', 'Lila Herz'], ['🖤', 'Schwarzes Herz'], ['🤍', 'Weißes Herz'],
      ['💕', 'Zwei Herzen'], ['💖', 'Funkelndes Herz'], ['💔', 'Gebrochenes Herz'], ['💯', 'Hundert Prozent'],
    ],
  },
  {
    title: 'Symbole & Alltag',
    emojis: [
      ['✅', 'Erledigt Haken'], ['❌', 'Fehler Kreuz'], ['⚠️', 'Warnung'], ['❓', 'Frage'],
      ['💡', 'Idee'], ['⭐', 'Stern'], ['✨', 'Funkeln'], ['🎉', 'Konfetti'],
      ['🚀', 'Rakete'], ['🔥', 'Feuer'], ['☕', 'Kaffee'], ['🍀', 'Glück'],
      ['📎', 'Anhang'], ['📅', 'Kalender'], ['⏰', 'Wecker Zeit'], ['💻', 'Computer'],
    ],
  },
];

const filteredGroups = computed(() => {
  const search = query.value.trim().toLocaleLowerCase('de');
  return groups.map(group => ({
    ...group,
    emojis: group.emojis.filter(([emoji, label]) =>
      `${group.title} ${label} ${emoji}`.toLocaleLowerCase('de').includes(search)),
  })).filter(group => group.emojis.length);
});

onMounted(() => searchInput.value?.focus({ preventScroll: true }));
</script>

<style scoped>
.emoji-picker {
  display: flex;
  flex-direction: column;
  max-height: min(320px, calc(100dvh - 240px));
  overflow: hidden;
  border: 1px solid #ededed;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 28px rgba(9, 14, 21, 0.12), 0 1px 4px rgba(9, 14, 21, 0.06);
  color: #14161a;
  font: 14px / 21px system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.emoji-search {
  display: flex;
  align-items: center;
  flex: none;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #ededed;
}

.emoji-search svg {
  flex: none;
  width: 18px;
  height: 18px;
  color: #6c6f74;
}

.emoji-search input {
  width: 100%;
  min-width: 0;
  padding: 4px;
  border: 0;
  border-radius: 4px;
  background: #fff;
  color: #14161a;
  font: inherit;
}

.emoji-search input::placeholder {
  color: #6c6f74;
}

.emoji-results {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 8px 12px;
}

.emoji-group h3 {
  margin: 8px 4px 4px;
  color: #6c6f74;
  font-size: 14px;
  font-weight: 500;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

.emoji-grid button {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  font: 24px / 1 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  cursor: pointer;
}

.emoji-grid button:hover {
  background: #f4f4f4;
}

.emoji-search input:focus {
  outline: none;
  box-shadow: none;
}

.emoji-grid button:focus-visible {
  outline: 2px solid #df4d44;
  outline-offset: -2px;
  box-shadow: none;
}

.emoji-empty {
  margin: 12px;
  color: #6c6f74;
}
</style>
